<template>
    <div class="checkout__cart-col">
        <div v-if="cartItems.length > 0">
            <!-- Warning if any unavailable -->
            <!--<div v-if="anyUnavailable" class="alert alert-warning mb-3">
                <strong>Delivery Notice:</strong>
                Some products in your cart are not available for delivery to your address.
            </div>-->

            <div class="cart-row cart-row--table">
                <div class="cart-row__heading">
                    <div class="cart-row__heading__lineaction"></div>
                    <div class="cart-row__heading__linetitle">Product</div>
                    <div class="cart-row__heading__lineprice">Price</div>
                    <div class="cart-row__heading__lineqty">Qty</div>
                    <div class="cart-row__heading__linetotal">Total</div>
                </div>

                <div
                    class="cart-item"
                    v-for="(item, idx) in cartItems"
                    :key="item.id"
                >
                    <div class="cart-item__actions">
                        <a href="#" class="cart-item__actions__link" @click.prevent="removeFromCart(idx)">
                            <svg focusable="false" role="img">
                                <use xlink:href="/build/assets/icons.svg#trash"/>
                            </svg>
                        </a>
                    </div>
                    <figure class="cart-item__image">
                        <img :src="item.image" alt="" />
                    </figure>
                    <div class="cart-item__details">
                        <div class="cart-item__details__info">
                            <h4 class="cart-item__details__title">{{ item.name }}</h4>
                            <div class="cart-item__details__subtitle">{{ item.variant }}</div>
                            <div class="cart-item__details__footnote">{{ item.note }}</div>
                            <!--<div
                                v-if="!isAvailable(item)"
                                class="delivery-unavailable-badge"
                            >
                                Not available for delivery
                            </div>-->
                        </div>
                        <div class="cart-item__details__price">
                            <div>{{ formatPrice(item.price) }}</div>
                        </div>
                        <div class="cart-item__details__control">
                            <button type="button" class="number-decrease" @click="decreaseQuantity(item)">-</button>
                            <input
                                type="number" 
                                min="0" 
                                :max="item.max_quantity || 999"
                                step="1"
                                v-model.number="item.quantity"
                                @input="enforceQuantityLimit(item)"
                                @change="enforceQuantityLimit(item); saveCart()"
                            />
                            <button 
                                type="button" 
                                class="number-increase" 
                                :disabled="item.max_quantity && item.quantity >= item.max_quantity"
                                @click="increaseQuantity(item)"
                            >+</button>
                        </div>
                    </div>
                    <div class="cart-item__price">
                        <div>{{ formatPrice(item.price * item.quantity) }}</div>
                    </div>
                </div>
            </div>

            <section v-if="showCheckoutAddOns" class="cart-add-ons" aria-labelledby="cart-add-ons-title">
                <div class="cart-add-ons__header">
                    <div>
                        <p class="cart-add-ons__eyebrow">Need anything else?</p>
                        <h3 id="cart-add-ons-title" class="cart-add-ons__title">Complete your order</h3>
                    </div>
                </div>

                <div class="cart-add-ons__items">
                    <article
                        v-for="addOn in checkoutAddOns"
                        :key="addOn.id"
                        class="cart-add-ons__item"
                    >
                        <figure class="cart-add-ons__image">
                            <img :src="addOn.image" alt="" />
                        </figure>

                        <div class="cart-add-ons__content">
                            <h4 class="cart-add-ons__item-title">{{ addOn.name }}</h4>
                            <p v-if="addOn.description" class="cart-add-ons__description">
                                {{ checkoutAddOnDescription(addOn) }}
                                <button
                                    v-if="shouldTruncateCheckoutAddOn(addOn)"
                                    type="button"
                                    class="cart-add-ons__description-more"
                                    @click="expandCheckoutAddOn(addOn.id)"
                                >
                                    More
                                </button>
                            </p>
                            <div class="cart-add-ons__variants">
                                <div
                                    v-for="variant in addOn.variants"
                                    :key="variant.id"
                                    class="cart-add-ons__variant"
                                >
                                    <div class="cart-add-ons__variant-details">
                                        <span v-if="variant.variant">{{ variant.variant }}</span>
                                        <span v-else>Default</span>
                                        <strong>{{ formatPrice(variant.price) }}</strong>
                                    </div>
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-rounded cart-add-ons__button"
                                        :disabled="isCheckoutAddOnInCart(variant)"
                                        @click="addCheckoutAddOnVariant(addOn, variant)"
                                    >
                                        {{ isCheckoutAddOnInCart(variant) ? 'Added' : 'Add' }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </article>
                </div>
            </section>

            <div class="cart-totals cart-totals--grandtotal cart-totals--table">

                <!-- Delivery Cost Row -->
                <div class="cart-totals__row">
                    <div class="cart-totals__row__label">
                        {{ selectedDeliveryOption ? (selectedDeliveryOption.id === 'pickup' ? 'Pickup' : 'Delivery') : 'Delivery' }}
                        <!--<div v-if="selectedDeliveryOption && selectedDeliveryOption.id !== 'pickup' && selectedDeliveryOption.title" class="cart-totals__row__subtext">
                            <small><nobr>{{ selectedDeliveryOption.title }}</nobr></small>
                        </div>-->
                    </div>
                    <div class="cart-totals__row__value">
                        {{ selectedDeliveryOption ? (selectedDeliveryOption.id === 'pickup' ? 'Free' : formatPrice(deliveryCost)) : 'TBC' }}
                    </div>
                </div>

                <!-- Discount Row (only show when applied) -->
                <div v-if="appliedDiscount" class="cart-totals__row">
                    <div class="cart-totals__row__label">
                        Discount <small class="text-muted">({{ appliedDiscount.code }})</small>
                    </div>
                    <div class="cart-totals__row__value discount-amount">
                        -{{ formatPrice(discountAmount) }}
                    </div>
                </div>

                <div class="cart-totals__row total-row">
                    <div class="cart-totals__row__label">Total</div>
                    <div class="cart-totals__row__value">{{ formatPrice(total) }}</div>
                </div>
                <div class="cart-totals__row gst-included">
                    <small class="text-muted">
                        <nobr>includes {{ formatPrice(gst) }} GST</nobr>
                    </small>
                </div>
            </div>

            <!-- Discount Code Input (always visible, below totals) -->
            <div class="discount-input-section">
                <div class="discount-section-header">
                    <h5 class="discount-heading">Have a discount code?</h5>
                    <p class="discount-subtext">Enter your code below to apply savings to your order.</p>
                </div>

                <div class="input-group">
                    <input
                        type="text"
                        v-model="discountCode"
                        :disabled="discountLoading || discountCooldown > 0"
                        @keyup.enter="appliedDiscount ? removeDiscount() : applyDiscount()"
                        :placeholder="appliedDiscount ? `${appliedDiscount.name} (${appliedDiscount.code}) applied` : 'Enter discount code'"
                        class="form-control"
                        :class="{ 'discount-applied-input': appliedDiscount }"
                        maxlength="50"
                        :readonly="appliedDiscount"
                    />
                    <button
                        type="button"
                        @click="appliedDiscount ? removeDiscount() : applyDiscount()"
                        :disabled="(!appliedDiscount && !discountCode.trim()) || discountLoading || discountCooldown > 0"
                        class="btn btn-sm"
                        :class="appliedDiscount ? 'btn-danger' : 'btn-primary'"
                    >
                        <span v-if="discountLoading">...</span>
                        <span v-else-if="discountCooldown > 0">{{ discountCooldown }}s</span>
                        <span v-else-if="appliedDiscount">Remove</span>
                        <span v-else>Apply</span>
                    </button>
                </div>

                <!-- Error/Success Messages -->
                <div v-if="discountMessage" class="discount-message" :class="discountMessageType">
                    <small>{{ discountMessage }}</small>
                </div>
            </div>
        </div>

        <div v-else class="empty-cart text-center py-5">
            <p class="mb-4">Your cart is currently empty.</p>
            <a href="/shop" class="btn btn-primary btn-rounded">Continue Shopping</a>
        </div>
    </div>
</template>
 
<script setup>
import { useCart } from '@/frontend/vue/composables/useCart'
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
    checkoutAddOnsEnabled: { type: Boolean, default: false },
    checkoutAddOns: { type: Array, default: () => [] },
})

const showCheckoutAddOns = computed(() => props.checkoutAddOnsEnabled && props.checkoutAddOns.length > 0)

const {
    cartItems,
    subtotal,
    gst,
    total,
    itemsTotal,
    deliveryCost,
    selectedDeliveryOption,
    deliveryConfig,
    appliedDiscount,
    discountAmount,
    decreaseQuantity,
    increaseQuantity,
    formatPrice,
    removeFromCart,
    loadCart,
    saveCart,
    applyDiscount: applyDiscountToCart,
    removeDiscount: removeDiscountFromCart,
    formatDiscountAmount
} = useCart()

// reactive cache of last API lookups
const lookupData = ref({})

// Discount functionality
const discountCode = ref('')
const discountLoading = ref(false)
const discountMessage = ref('')
const discountMessageType = ref('') // 'success' or 'error'
const discountFailedAttempts = ref(0)
const discountCooldown = ref(0)
const cooldownTimer = ref(null)
const expandedCheckoutAddOns = ref({})
const checkoutAddOnDescriptionLimit = 110

function loadLookups() {
    try {
        lookupData.value = JSON.parse(localStorage.getItem('ec_addr_lookup') || '{}')
    } catch {
        lookupData.value = {}
    }
}

// check single item
function isAvailable(item) {
    // If delivery is turned off, always return true (item is available)
    if (deliveryConfig.value && !deliveryConfig.value.deliveryEnabled) {
        return true;
    }
    const key = item.handle || item.code
    const rec = lookupData.value[key]
    return rec?.available === true
}

// any / all helpers
const anyUnavailable = computed(() => {
    // If delivery is turned off, never show unavailable message
    if (deliveryConfig.value && !deliveryConfig.value.deliveryEnabled) {
        return false;
    }
    return cartItems.value.some(item => !isAvailable(item));
})

function checkoutAddOnCartLine(variant) {
    return cartItems.value.find(item => item.type === (variant.type || 'variant') && item.id === variant.id)
}

function isCheckoutAddOnInCart(variant) {
    return !!checkoutAddOnCartLine(variant)
}

function addCheckoutAddOnVariant(addOn, variant) {
    if (isCheckoutAddOnInCart(variant)) {
        return
    }

    cartItems.value.push({
        id: variant.id,
        type: variant.type || 'variant',
        name: addOn.name,
        handle: addOn.handle,
        price: variant.price,
        quantity: 1,
        variant: variant.variant || 'Default',
        code: variant.code,
        image: addOn.image,
        max_quantity: variant.max_quantity,
    })

    saveCart()
}

function normalizedCheckoutAddOnDescription(addOn) {
    return String(addOn.description || '')
        .replace(/\s+/g, ' ')
        .trim()
}

function shouldTruncateCheckoutAddOn(addOn) {
    const description = normalizedCheckoutAddOnDescription(addOn)
    return !expandedCheckoutAddOns.value[addOn.id] && description.length > checkoutAddOnDescriptionLimit
}

function checkoutAddOnDescription(addOn) {
    const description = normalizedCheckoutAddOnDescription(addOn)

    if (expandedCheckoutAddOns.value[addOn.id] || description.length <= checkoutAddOnDescriptionLimit) {
        return description
    }

    return `${description.slice(0, checkoutAddOnDescriptionLimit).trimEnd()}...`
}

function expandCheckoutAddOn(addOnId) {
    expandedCheckoutAddOns.value = {
        ...expandedCheckoutAddOns.value,
        [addOnId]: true,
    }
}

// Discount functions
async function applyDiscount() {
    if (!discountCode.value.trim() || discountLoading.value || discountCooldown.value > 0) {
        return
    }

    discountLoading.value = true
    discountMessage.value = ''

    try {
        const response = await fetch(`/api/discount/code/${encodeURIComponent(discountCode.value.trim())}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }
        })

        const data = await response.json()

        if (data.success) {
            applyDiscountToCart(data.data)
            discountMessage.value = ''
            discountMessageType.value = ''
            discountCode.value = ''
            discountFailedAttempts.value = 0
        } else {
            handleDiscountError(data.message || 'Invalid discount code')
        }
    } catch (error) {
        handleDiscountError('Unable to validate discount code. Please try again.')
    } finally {
        discountLoading.value = false
    }
}

function handleDiscountError(message) {
    discountFailedAttempts.value++
    discountMessage.value = message
    discountMessageType.value = 'error'

    // After 3 failed attempts, start cooldown
    if (discountFailedAttempts.value >= 3) {
        startCooldown()
    }
}

function startCooldown() {
    discountCooldown.value = 30 // 30 second cooldown
    discountMessage.value = 'Too many failed attempts. Please wait 30 seconds before trying again.'

    cooldownTimer.value = setInterval(() => {
        discountCooldown.value--
        if (discountCooldown.value <= 0) {
            clearInterval(cooldownTimer.value)
            discountFailedAttempts.value = 0
            discountMessage.value = ''
        }
    }, 1000)
}

function removeDiscount() {
    removeDiscountFromCart()
    discountMessage.value = ''
    discountCode.value = ''
    discountMessageType.value = ''
}

function enforceQuantityLimit(item) {
    if (item.max_quantity && item.quantity > item.max_quantity) {
        item.quantity = item.max_quantity
    }
    if (item.quantity < 0) {
        item.quantity = 0
    }
}


onMounted(() => {
    loadCart()
    loadLookups()

    // update when other tabs change it
    window.addEventListener('storage', loadLookups)
    // update when your first component fires this
    window.addEventListener('ec:availability-updated', loadLookups)
})

onUnmounted(() => {
    window.removeEventListener('storage', loadLookups)
    window.removeEventListener('ec:availability-updated', loadLookups)

    // Clean up cooldown timer
    if (cooldownTimer.value) {
        clearInterval(cooldownTimer.value)
    }
})
</script>

<style scoped>
.cart-row__heading__lineprice,
.cart-row__heading__linetotal {
    text-align: right;
    justify-content: right;
}

.empty-cart { color: #666; }

.gst-included {
    justify-content: end;
    font-weight: normal;
    color: #333;
    margin-top: 5px;
}

.total-row {
    font-weight: bold;
    font-size: 1.1em;
    margin-top: 10px;
    border-top: 1px solid #ddd;
    padding-top: 10px;
}

.delivery-unavailable-badge {
    display: inline-block;
    font-size: 0.75rem;
    background-color: #dc3545;
    color: #fff;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    margin-top: 0.5rem;
}

.cart-totals__row__subtext {
    font-weight: normal;
    color: #666;
    margin-top: 2px;
}

.alert { position: relative; padding: 0.75rem 1.25rem; border-radius: 0.25rem; }
.alert-warning {
    color: #856404;
    background-color: #fff3cd;
    border-color: #ffeeba;
    margin-bottom: 1.5rem;
}

.cart-add-ons {
    margin-top: 24px;
    margin-bottom: 24px;
    padding: 18px;
    border: 1px solid var(--cart-add-ons-border-color, var(--config-color-gray-200, #eee));
    border-radius: 14px;
    background: #fff;
}

.cart-add-ons__header {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 16px;
}

.cart-add-ons__eyebrow {
    margin: 0 0 4px;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--config-color-primary, #333);
}

.cart-add-ons__title {
    margin: 0;
    font-size: 1.15rem;
}

.cart-add-ons__intro {
    max-width: 260px;
    margin: 0;
    color: #666;
    font-size: 0.9rem;
    line-height: 1.4;
}

.cart-add-ons__items {
    display: grid;
    gap: 12px;
}

.cart-add-ons__item {
    display: grid;
    grid-template-columns: 72px minmax(0, 1fr);
    align-items: start;
    gap: 14px;
    padding: 12px;
    border: 1px solid #eee;
    border-radius: 12px;
    background: #fafafa;
}

.cart-add-ons__image {
    aspect-ratio: 1;
    margin: 0;
    overflow: hidden;
    border-radius: 10px;
    background: #f2f2f2;
}

.cart-add-ons__image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.cart-add-ons__item-title {
    margin: 0 0 4px;
    font-size: 1rem;
}

.cart-add-ons__description {
    margin: 0 0 6px;
    color: #666;
    font-size: 0.88rem;
    line-height: 1.35;
}

.cart-add-ons__description-more {
    display: inline;
    margin-left: 0.35rem;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--config-color-primary, #333);
    font: inherit;
    font-weight: 700;
    cursor: pointer;
    text-decoration: underline;
}

.cart-add-ons__variants {
    display: grid;
    gap: 8px;
    margin-top: 10px;
}

.cart-add-ons__variant {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    background: #fff;
    border: 1px solid var(--config-color-primary-50, #aaa);
    padding: 8px 7px;
    border-radius: 10px;
}

.cart-add-ons__variant-details {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    color: #555;
    font-size: 0.9rem;
}

.cart-add-ons__variant-details span:after {
    content: ':';
}

.cart-add-ons__price {
    font-weight: 700;
}

.cart-add-ons__button {
    align-self: center;
    white-space: nowrap;
}

@media (max-width: 767px) {
    .cart-add-ons__header {
        display: block;
    }

    .cart-add-ons__intro {
        max-width: none;
        margin-top: 8px;
    }

    .cart-add-ons__item {
        grid-template-columns: 64px minmax(0, 1fr);
    }

    .cart-add-ons__button {
        justify-self: flex-start;
    }
}

/* Discount styles */
.discount-input-section {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #eee;
}

.discount-section-header {
    margin-bottom: 15px;
}

.discount-heading {
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    margin: 0 0 5px 0;
}

.discount-subtext {
    font-size: 0.9rem;
    color: #666;
    margin: 0;
    line-height: 1.4;
}

.input-group {
    display: flex;
    margin-bottom: 10px;
}

.input-group .form-control {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: 0;
}

.input-group .btn {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}

.discount-applied-input {
    background-color: #d4edda !important;
    color: var(--config-cart-link-bg) !important;
    font-weight: 500;
}

.discount-amount {
    color: var(--config-cart-link-bg);
}

.discount-message {
    padding: 0.5rem 0.75rem;
    border-radius: 0.25rem;
    font-size: 0.875rem;
    margin-top: 0.5rem;
}

.discount-message.success {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
}

.discount-message.error {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
}
</style>
